import { ModalCloseButton, ModalShell } from '@busy/ui';
import { useT } from '../../lib/i18n';

// Opens from the Revenue metric card on the dashboard. Splits the
// headline number (cuts + tips combined) into its two components so
// the barber can answer "how much of my revenue is service vs
// gratuity?" — a question the headline figure intentionally combines
// for at-a-glance scanning.
//
// Replaces the prior behavior where tapping the metric card scrolled
// down to the Revenue-by-day chart. A jump-to-chart link still lives
// inside this modal as a secondary action for users who came here
// looking for that.

type Props = {
  rangeLabel: string;
  cutsRevenueCents: number;
  tipsCents: number;
  visitsInRange: number;
  onClose: () => void;
  /** Optional — when wired, the modal shows a "See revenue by day"
   *  link that closes the modal and scrolls the chart into view. */
  onViewChart?: () => void;
};

export function RevenueBreakdownModal({
  rangeLabel,
  cutsRevenueCents,
  tipsCents,
  visitsInRange,
  onClose,
  onViewChart,
}: Props) {
  const t = useT();
  const totalCents = cutsRevenueCents + tipsCents;
  const cutsPct =
    totalCents > 0 ? Math.round((cutsRevenueCents / totalCents) * 100) : 0;
  const tipsPct = totalCents > 0 ? 100 - cutsPct : 0;

  // Per-visit average tip — only meaningful when there's been at least
  // one visit, otherwise the denominator is zero and the number reads
  // nonsense. Shown in the tip row as soft context, not a headline.
  const avgTipPerVisitCents =
    visitsInRange > 0 ? Math.round(tipsCents / visitsInRange) : 0;

  return (
    <ModalShell open onClose={onClose} size="md" ariaLabel={t('dash.revBreakdown.aria')}>
      <ModalCloseButton onClose={onClose} />
      <div className="px-6 pt-7 pb-4 border-b border-line">
        <div className="text-[11px] tracking-[0.18em] uppercase font-extrabold text-gold-500 mb-1.5 inline-flex items-center gap-1.5">
          <DollarIcon className="w-3 h-3" />
          {t('dash.revBreakdown.eyebrow')}
        </div>
        <h2 className="font-display text-2xl leading-tight">
          {t('dash.revBreakdown.title')}
        </h2>
        <p className="text-[12px] text-ink-500 mt-1">
          {t('dash.revBreakdown.sub', { range: rangeLabel })}
        </p>
      </div>

      <div className="p-5 space-y-4">
        {/* Composite bar — visual share. When there's no revenue yet,
            renders as a flat track with the empty message below, so the
            chart placeholder doesn't read as "100% cuts" or "100% tips". */}
        {totalCents > 0 ? (
          <div className="h-3 rounded-full bg-line overflow-hidden flex">
            <div
              className="h-full bg-navy-900 dark:bg-gold-500"
              style={{ width: `${cutsPct}%` }}
              aria-hidden
            />
            <div
              className="h-full bg-gold-500 dark:bg-gold-300"
              style={{ width: `${tipsPct}%` }}
              aria-hidden
            />
          </div>
        ) : (
          <div className="h-3 rounded-full bg-line" aria-hidden />
        )}

        <div className="space-y-3">
          <BreakdownRow
            swatchClass="bg-navy-900 dark:bg-gold-500"
            label={t('dash.revBreakdown.cuts')}
            sub={t('dash.revBreakdown.cutsHelp')}
            dollars={cutsRevenueCents / 100}
            pct={cutsPct}
            visible={totalCents > 0}
          />
          <BreakdownRow
            swatchClass="bg-gold-500 dark:bg-gold-300"
            label={t('dash.revBreakdown.tips')}
            sub={
              visitsInRange > 0
                ? t('dash.revBreakdown.tipsHelp', {
                    avg: `$${(avgTipPerVisitCents / 100).toFixed(
                      avgTipPerVisitCents % 100 === 0 ? 0 : 2,
                    )}`,
                  })
                : t('dash.revBreakdown.tipsHelpEmpty')
            }
            dollars={tipsCents / 100}
            pct={tipsPct}
            visible={totalCents > 0}
          />
        </div>

        {/* Total row — heavy weight + top border so it reads as the
            footing of the breakdown, not just another line. */}
        <div className="flex items-center justify-between border-t border-line pt-3">
          <span className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500">
            {t('dash.revBreakdown.total')}
          </span>
          <span className="font-display text-2xl text-navy-900 dark:text-gold-500 tabular-nums">
            ${(totalCents / 100).toFixed(totalCents % 100 === 0 ? 0 : 2)}
          </span>
        </div>

        {/* Policy footnote — quiet reminder that tips don't count
            toward the revenue achievement (deliberate split from
            migration 0065's header). The user will hit this question
            eventually; better to answer it inline. */}
        <p className="text-[11px] text-ink-500 leading-snug">
          {t('dash.revBreakdown.tipsPolicy')}
        </p>

        {onViewChart && (
          <button
            type="button"
            onClick={onViewChart}
            className="w-full h-11 rounded-lg border border-line bg-surface text-sm font-bold text-navy-900 dark:text-gold-500 hover:bg-bg-alt transition-colors"
          >
            {t('dash.revBreakdown.viewChart')}
          </button>
        )}
      </div>
    </ModalShell>
  );
}

function BreakdownRow({
  swatchClass,
  label,
  sub,
  dollars,
  pct,
  visible,
}: {
  swatchClass: string;
  label: string;
  sub: string;
  dollars: number;
  pct: number;
  visible: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className={`w-3 h-3 rounded-sm mt-1 shrink-0 ${swatchClass}`} aria-hidden />
      <div className="flex-1 min-w-0">
        <strong className="block font-display text-base leading-tight">{label}</strong>
        <span className="text-[11px] text-ink-500 leading-snug">{sub}</span>
      </div>
      <div className="text-right shrink-0 tabular-nums">
        <div className="font-display text-lg text-ink-900 leading-tight">
          ${dollars.toFixed(dollars % 1 === 0 ? 0 : 2)}
        </div>
        {visible && (
          <div className="text-[10px] uppercase tracking-[0.08em] font-bold text-ink-500">
            {pct}%
          </div>
        )}
      </div>
    </div>
  );
}

function DollarIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="12" y1="1" x2="12" y2="23" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}
